import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { findTrail, type NavSection } from "@strivelabs/ui-blocks/v2";
import { getBreadcrumbRoot, getBreadcrumbData } from "@/constants/navigation";
import { ANALYTICS_EVENTS, useTrack } from "@/lib/analytics";
import { usePageChromeTitle, usePageChromeParent, useHeaderActionsSlot } from "@/components/layout/PageChrome";

/**
 * A full-height, slightly-widened pointer target on an otherwise text-height
 * link, added through a pseudo-element so it doesn't disturb truncation or the
 * row's layout. `-inset-x-1` stays under the `gap-2` between crumbs, so adjacent
 * targets never overlap.
 */
const HIT_AREA = "relative before:absolute before:-inset-y-4 before:-inset-x-1 before:content-['']";
const LINK_BASE =
  "rounded-v2-sm text-v2-muted-foreground transition-colors hover:text-v2-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring";

/**
 * The persistent breadcrumb header. Derives its trail from the nav tree so the
 * breadcrumb and the sidebar can never disagree; a dynamic leaf title (a report
 * name, a skill) and page-level actions come from PageChrome. Rendered once by
 * {@link AppShell}, above the routed content.
 *
 * A dynamic title is a leaf *beyond* the matched nav node — the whole trail
 * becomes ancestors and the title is the terminal crumb (Home ▸ Reports ▸ Q3
 * report), rather than replacing the matched node's own label.
 */
export function PageHeader({ sections }: { sections: NavSection[] }) {
  const { pathname } = useLocation();
  const title = usePageChromeTitle();
  const parent = usePageChromeParent();
  const setActionsSlot = useHeaderActionsSlot();
  const { href: homeHref } = getBreadcrumbRoot();
  const trackEvent = useTrack();

  // `level` says which crumb was used: whether people climb one step up
  // (parent) or jump to the top (home). The origin page comes free from
  // `route_pattern`.
  const trackCrumb = (level: "home" | "ancestor" | "parent") => {
    trackEvent(ANALYTICS_EVENTS.BREADCRUMB_CLICKED, { level });
  };

  const trail = findTrail(sections, pathname);
  const hasDynamicLeaf = Boolean(title);
  const ancestors = hasDynamicLeaf ? trail : trail.slice(0, -1);
  const leafLabel = title ?? trail.at(-1)?.label ?? getBreadcrumbData(pathname).currentPageLabel;
  const isHome = pathname === homeHref;

  // The home lobby has no breadcrumb (Home Shell design): the greeting is the
  // page's own heading and the wash runs edge to edge under it.
  if (isHome) return null;

  return (
    <header className="flex h-[52px] shrink-0 items-center gap-2 border-b border-v2-border bg-v2-page px-5">
      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-2 text-sm">
        <Link
          to={homeHref}
          onClick={() => trackCrumb("home")}
          aria-label="Home"
          className={`${HIT_AREA} ${LINK_BASE} shrink-0`}
        >
          <Home className="size-4" />
        </Link>
        {ancestors.map((node) => (
          <Fragment key={node.id}>
            <ChevronRight aria-hidden className="size-3.5 shrink-0 text-v2-muted-foreground/60" />
            {node.href && node.href !== "#" ? (
              <Link
                to={node.href}
                onClick={() => trackCrumb("ancestor")}
                className={`${HIT_AREA} ${LINK_BASE} min-w-0 truncate`}
              >
                {node.label}
              </Link>
            ) : (
              <span className="min-w-0 truncate text-v2-muted-foreground">{node.label}</span>
            )}
          </Fragment>
        ))}
        {parent ? (
          <>
            <ChevronRight aria-hidden className="size-3.5 shrink-0 text-v2-muted-foreground/60" />
            {parent.href ? (
              <Link
                to={parent.href}
                onClick={() => trackCrumb("parent")}
                className={`${HIT_AREA} ${LINK_BASE} min-w-0 truncate`}
              >
                {parent.label}
              </Link>
            ) : (
              <span className="min-w-0 truncate text-v2-muted-foreground">{parent.label}</span>
            )}
          </>
        ) : null}
        {leafLabel ? (
          <>
            <ChevronRight aria-hidden className="size-3.5 shrink-0 text-v2-muted-foreground/60" />
            <span
              aria-current="page"
              className="min-w-0 truncate font-semibold text-v2-foreground"
              title={leafLabel}
            >
              {leafLabel}
            </span>
          </>
        ) : null}
      </nav>
      <div ref={setActionsSlot} className="flex shrink-0 items-center gap-2" />
    </header>
  );
}
